import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { MapPin, Camera, PenLine, Send, QrCode, Wifi, WifiOff } from "lucide-react";

export default function InspectionPage() {
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState({ project_id: "", site_name: "", inspector_name: "", latitude: 30.9, longitude: 75.85, findings: "" });
  const [msg, setMsg] = useState("");
  const [gpsBusy, setGpsBusy] = useState(false);

  useEffect(() => {
    api.get("/inspections").then((r)=>setItems(r.data.items));
    api.get("/projects").then((r)=>setProjects(r.data.items.slice(0, 30)));
  }, []);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const captureGPS = () => {
    setGpsBusy(true);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setForm(f => ({...f, latitude: pos.coords.latitude, longitude: pos.coords.longitude })); setGpsBusy(false); },
        () => { setMsg("Could not fetch GPS. Using default."); setGpsBusy(false); },
        { enableHighAccuracy: true, timeout: 8000 }
      );
    } else { setGpsBusy(false); setMsg("Geolocation not supported."); }
  };

  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    try {
      await api.post("/inspections", { ...form, latitude: Number(form.latitude), longitude: Number(form.longitude), photos: [] });
      setMsg("Inspection submitted!");
      api.get("/inspections").then((r)=>setItems(r.data.items));
      setForm({ ...form, findings: "" });
    } catch (e) { setMsg(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="label-tiny text-[hsl(var(--primary))]">Field Operations</div>
        <h1 className="mt-1 text-3xl font-heading font-bold">Geo-Tagged Inspection</h1>
        <p className="text-sm text-muted-foreground mt-1">Optimized for mobile · GPS + photo + digital signature capture</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-4">
        <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-5">
          <div className="flex items-center gap-2 mb-3">
            <div className="stat-pill bg-emerald-500/10 text-emerald-500"><Wifi className="h-3 w-3"/>Live sync ON</div>
            <div className="stat-pill bg-slate-500/10 text-slate-500"><WifiOff className="h-3 w-3"/>Offline queue</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2">
              <span className="text-xs font-semibold text-muted-foreground">Project</span>
              <select required value={form.project_id} onChange={change("project_id")} className="input mt-1">
                <option value="">-- Select project --</option>
                {projects.map(p=><option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label>
              <span className="text-xs font-semibold text-muted-foreground">Inspector</span>
              <input required value={form.inspector_name} onChange={change("inspector_name")} className="input mt-1"/>
            </label>
            <label>
              <span className="text-xs font-semibold text-muted-foreground">Site name</span>
              <input required value={form.site_name} onChange={change("site_name")} className="input mt-1"/>
            </label>
            <label>
              <span className="text-xs font-semibold text-muted-foreground">Latitude</span>
              <input value={form.latitude} onChange={change("latitude")} className="input mt-1"/>
            </label>
            <label>
              <span className="text-xs font-semibold text-muted-foreground">Longitude</span>
              <input value={form.longitude} onChange={change("longitude")} className="input mt-1"/>
            </label>
            <label className="col-span-2">
              <span className="text-xs font-semibold text-muted-foreground">Findings</span>
              <textarea rows={3} required value={form.findings} onChange={change("findings")} className="input mt-1"/>
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" onClick={captureGPS} className="btn-outline text-xs" disabled={gpsBusy}><MapPin className="h-3.5 w-3.5"/>{gpsBusy ? "Locating…" : "Capture GPS"}</button>
            <button type="button" className="btn-outline text-xs"><Camera className="h-3.5 w-3.5"/>Photos</button>
            <button type="button" className="btn-outline text-xs"><QrCode className="h-3.5 w-3.5"/>Scan QR</button>
            <button type="button" className="btn-outline text-xs"><PenLine className="h-3.5 w-3.5"/>Signature</button>
            <button type="submit" className="btn-primary text-xs ml-auto"><Send className="h-3.5 w-3.5"/>Submit</button>
          </div>
          {msg && <div className="mt-3 text-sm">{msg}</div>}
        </form>
        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="font-heading font-semibold mb-3">Recent Inspections</div>
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {items.map(i => (
              <div key={i.id} className="rounded-lg border border-border p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <MapPin className="h-3 w-3"/>{i.latitude?.toFixed(3)}, {i.longitude?.toFixed(3)}
                  <span className={`ml-auto stat-pill capitalize ${i.status === "approved" ? "bg-emerald-500/10 text-emerald-500" : i.status === "review" ? "bg-amber-500/10 text-amber-500" : "bg-sky-500/10 text-sky-500"}`}>{i.status}</span>
                </div>
                <div className="mt-1 font-medium">{i.site_name}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Inspector: {i.inspector_name}</div>
                <div className="mt-2 text-sm">{i.findings}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
